import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MIN_PASSWORD_LENGTH, describePermissions, type DeviceSummary } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { SectionNavLayout, wideScreen, type SectionNavGroup } from '../components/SectionNavLayout';
import { useMyEmployee } from '../modules/hr';

interface AccountLink {
  path: string;
  label: string;
  keywords: string[];
}

const fold = (text: string) => text.toLocaleLowerCase('de-DE').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

/**
 * Bereiche von „Mein Konto“ (KON-122). „Ich als Mitarbeiter“ erscheint, wenn der Zugang mit einem Mitarbeiter verknüpft ist oder der Mandant das Modul
 * Mitarbeiter hat. Ohne Verknüpfung steht dort nur „Mitarbeiterdaten“ mit einem Hinweis.
 */
function useAccountGroups(): { key: string; label: string; links: AccountLink[] }[] {
  const { hasModule } = useSession();
  const mine = useMyEmployee();
  const groups = [
    {
      key: 'general',
      label: 'Allgemein',
      links: [
        { path: 'angaben', label: 'Meine Angaben', keywords: ['Name', 'Unternehmen', 'Mandantennummer', 'Rolle'] },
        { path: 'rechte', label: 'Meine Rechte', keywords: ['Berechtigungen', 'Rolle', 'Zugriff'] },
        { path: 'zugang', label: 'Zugangsdaten', keywords: ['E-Mail', 'Adresse', 'Passwort', 'Kennwort', 'Sicherheit', 'Anmeldung'] },
      ],
    },
  ];
  if (mine.data) {
    groups.push({
      key: 'employee',
      label: 'Ich als Mitarbeiter',
      links: [
        { path: 'mitarbeiter', label: 'Mitarbeiterdaten', keywords: ['Personalnummer', 'Position', 'Abteilung', 'Eintritt', 'Profilfoto', 'Foto', 'Bild'] },
        { path: 'urlaub', label: 'Urlaub und Freistellung', keywords: ['Urlaubsanspruch', 'Resturlaub', 'Antrag', 'beantragen', 'Feiertage', 'frei'] },
        { path: 'antraege', label: 'Meine Anträge', keywords: ['Freistellung', 'Urlaub', 'Stand', 'genehmigt', 'abgelehnt', 'zurückziehen'] },
        ...(hasModule('shifts') ? [{ path: 'schichten', label: 'Mein Schichtplan', keywords: ['Schicht', 'Dienstplan', 'Arbeitszeit', 'Einsatz', 'Wochenplan'] }] : []),
        { path: 'dateien', label: 'Dokumente', keywords: ['Vertrag', 'Arbeitsvertrag', 'Dokumente', 'herunterladen'] },
      ],
    });
  } else if (!mine.isLoading && hasModule('hr')) {
    groups.push({ key: 'employee', label: 'Ich als Mitarbeiter', links: [{ path: 'mitarbeiter', label: 'Mitarbeiterdaten', keywords: ['Personalnummer', 'Urlaub', 'Verträge'] }] });
  }
  return groups;
}

function searchGroups(groups: ReturnType<typeof useAccountGroups>, query: string): SectionNavGroup[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  return groups
    .map((g) => ({ key: g.key, label: g.label, links: g.links.filter((l) => words.every((w) => fold([g.label, l.label, ...l.keywords].join(' ')).includes(w))) }))
    .filter((g) => g.links.length > 0);
}

/** Rahmen von „Mein Konto“ mit derselben Seitennavigation wie die Einrichtung (KON-122). Anfangs ist nur „Allgemein“ aufgeklappt. */
export function AccountLayout() {
  const groups = useAccountGroups();
  return (
    <SectionNavLayout
      title="Mein Konto"
      basePath="/konto"
      groupsFor={(query) => searchGroups(groups, query)}
      storageKey="kontor:account-open:v2"
      defaultOpen={[]}
      navLabel="Bereiche von Mein Konto"
      searchLabel="In Mein Konto suchen"
      backLabel="Alle Bereiche von Mein Konto"
    />
  );
}

/** /konto: Auf breiten Bildschirmen geht es gleich zu „Meine Angaben“, auf schmalen steht dort nur die Navigation */
export function AccountIndex() {
  return wideScreen() ? <Navigate to="angaben" replace /> : null;
}

/** Unternehmen, Mandantennummer und Rolle zur Ansicht, den eigenen Namen ändert der Benutzer selbst (KON-123) */
export function AccountDetailsPage() {
  const { session } = useSession();
  const client = useQueryClient();
  const [name, setName] = useState(session?.user.name ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('PATCH', '/auth/profile', { name }),
    onSuccess: () => {
      setError(null);
      client.invalidateQueries({ queryKey: ['session'] });
    },
    onError: (e) => setError(e instanceof ApiError ? (e.fields.name ?? e.message) : 'Der Name konnte nicht gespeichert werden.'),
  });
  if (!session) return null;

  const changed = name.trim() !== session.user.name;
  function submit(event: FormEvent) {
    event.preventDefault();
    save.reset();
    if (name.trim().length < 2) return setError('Der Name braucht mindestens 2 Zeichen.');
    save.mutate();
  }

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Meine Angaben</h2>
      {save.isSuccess && !changed && <div className="alert alert--ok" role="status">Ihr Name wurde gespeichert.</div>}
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" aria-invalid={!!error} />
          {error && <small className="field__error" role="alert">{error}</small>}
        </label>
      </div>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending || !changed}>{save.isPending ? 'Wird gespeichert …' : 'Name speichern'}</button>
      </div>
      <dl className="facts account-facts">
        <dt>E-Mail</dt><dd>{session.user.email} · <Link to="/konto/zugang">unter „Zugangsdaten“ ändern</Link></dd>
        <dt>Unternehmen</dt><dd>{session.tenant.displayName}</dd>
        <dt>Mandantennummer</dt><dd>{session.tenant.number}</dd>
        <dt>Rolle</dt><dd>{session.user.role.name}</dd>
      </dl>
      <p className="muted small">Die Rolle vergibt ein Administrator unter Einrichtung → Benutzer. Sie melden sich mit der Mandantennummer, Ihrer E-Mail-Adresse und Ihrem Passwort an.</p>
    </form>
  );
}

/** Die Rechte der eigenen Rolle, nach Modulen */
export function AccountRightsPage() {
  const { session } = useSession();
  if (!session) return null;
  const rights = describePermissions(session.user.permissions);
  return (
    <section className="card">
      <h2>Meine Rechte</h2>
      <p className="muted">Ihre Rolle „{session.user.role.name}“ erlaubt Folgendes:</p>
      {rights.length === 0 ? (
        <p className="muted">Ihre Rolle hat keine Rechte an Modulen.</p>
      ) : (
        <ul className="rights">
          {rights.map((r) => <li key={r.module}><strong>{r.label}:</strong> {r.actions.join(', ')}</li>)}
        </ul>
      )}
    </section>
  );
}

/** Zugangsdaten (KON-123): E-Mail-Adresse und Passwort, beides nur mit dem aktuellen Passwort */
export function AccountAccessPage() {
  return (
    <div className="account-stack">
      <EmailForm />
      <PasswordForm />
      <DevicesCard />
    </div>
  );
}

/**
 * Die E-Mail-Adresse ist der Anmeldename. Wirkia prüft, ob sie im Unternehmen frei ist, beendet andere Anmeldungen und schreibt an die alte Adresse.
 * Beim verknüpften Mitarbeiter steht danach die neue Adresse.
 */
function EmailForm() {
  const { session } = useSession();
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [current, setCurrent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<{ email: string; noticeSent: boolean } | null>(null);

  const change = useMutation({
    mutationFn: async () => (await api<{ data: { email: string; noticeSent: boolean } }>('POST', '/auth/email', { email, currentPassword: current })).data,
    onSuccess: (r) => {
      setEmail('');
      setCurrent('');
      setDone(r);
      client.invalidateQueries({ queryKey: ['session'] });
      client.invalidateQueries({ queryKey: ['me-employee'] });
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        setErrors(e.status === 422 ? e.fields : {});
        setFormError(e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.' : e.status === 422 ? null : e.message);
      } else {
        setFormError('Die E-Mail-Adresse konnte nicht geändert werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    setDone(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setErrors({ email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' });
    change.mutate();
  }
  const fieldError = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>E-Mail-Adresse ändern</h2>
      <p className="muted">Ihre E-Mail-Adresse ist zugleich Ihr Anmeldename. Zurzeit: <strong>{session?.user.email}</strong></p>
      {done && <div className="alert alert--ok" role="status">Ihre E-Mail-Adresse ist jetzt {done.email}. Melden Sie sich ab sofort damit an. Anmeldungen auf anderen Geräten sind beendet.{done.noticeSent ? ' An die alte Adresse ist ein Hinweis gegangen.' : ''}</div>}
      {formError && <div className="alert alert--error" role="alert">{formError}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Neue E-Mail-Adresse</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" aria-invalid={!!errors.email} />
          {fieldError('email')}
        </label>
        <label className="field">
          <span>Aktuelles Passwort</span>
          <input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" aria-invalid={!!errors.currentPassword} />
          {fieldError('currentPassword')}
        </label>
      </div>
      <p className="muted small">Ist ein Mitarbeiter mit Ihrem Zugang verknüpft, steht die neue Adresse auch dort.</p>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={change.isPending || !email || !current}>{change.isPending ? 'Wird gespeichert …' : 'E-Mail-Adresse ändern'}</button>
      </div>
    </form>
  );
}

/** Das eigene Passwort ändern. Andere Anmeldungen enden dabei, diese bleibt. */
const dateTime = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/**
 * Handys, auf denen Sie die Wirkia App angemeldet haben (KON-232). Hier schalten Sie die Push-Nachricht bei neuen Chats für ein Handy aus oder ein, und hier melden Sie ein verlorenes
 * Handy ab: Die Anmeldung der App endet sofort, und es bekommt keine Nachrichten mehr.
 */
function DevicesCard() {
  const client = useQueryClient();
  const devices = useQuery({ queryKey: ['devices'], queryFn: async () => (await api<{ data: DeviceSummary[] }>('GET', '/devices')).data });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ['devices'] });
  const toggle = useMutation({
    mutationFn: (v: { id: string; chatPush: boolean }) => api('PATCH', `/devices/${v.id}`, { chatPush: v.chatPush }),
    onSuccess: refresh,
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Die Änderung hat nicht geklappt.' }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/devices/${id}?endSession=1`),
    onSuccess: () => {
      refresh();
      setMessage({ ok: true, text: 'Das Handy ist abgemeldet. Es bekommt keine Nachrichten mehr und muss sich neu anmelden.' });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Das Abmelden hat nicht geklappt.' }),
  });

  if (devices.isLoading) return null;
  const list = devices.data ?? [];
  if (list.length === 0 && !devices.isError) {
    return (
      <section className="card">
        <h2>Wirkia App</h2>
        <p className="muted">Auf Ihrem Konto ist noch kein Handy mit der Wirkia App angemeldet. Angemeldete Handys erscheinen hier.</p>
      </section>
    );
  }
  return (
    <section className="card">
      <h2>Wirkia App</h2>
      <p className="muted">Handys, auf denen Sie die Wirkia App angemeldet haben. Ein verlorenes Handy melden Sie hier ab.</p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {devices.isError && <div className="alert alert--error" role="alert">Die Handys konnten nicht geladen werden.</div>}
      <ul className="device-list">
        {list.map((d) => (
          <li key={d.id} className="device-list__item">
            <div>
              <strong>{d.deviceName ?? 'Handy'}</strong>
              <div className="muted small">{d.platform === 'android' ? 'Android' : 'iPhone'}{d.appVersion ? `, App ${d.appVersion}` : ''} · zuletzt benutzt {dateTime.format(new Date(d.lastSeenAt))}</div>
            </div>
            <label className="check">
              <input type="checkbox" checked={d.chatPush} disabled={toggle.isPending} onChange={(e) => toggle.mutate({ id: d.id, chatPush: e.target.checked })} />
              <span>Push bei neuen Chats</span>
            </label>
            <button
              className="btn btn--secondary btn--sm"
              type="button"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`„${d.deviceName ?? 'Handy'}“ abmelden? Die App auf diesem Handy ist danach nicht mehr angemeldet.`)) remove.mutate(d.id);
              }}
            >
              Abmelden
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => api('POST', '/auth/password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setRepeat('');
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        setErrors(e.status === 422 ? e.fields : {});
        setFormError(e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.' : e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message);
      } else {
        setFormError('Das Passwort konnte nicht geändert werden.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    change.reset();
    setErrors({});
    setFormError(null);
    if (next.length < MIN_PASSWORD_LENGTH) return setErrors({ newPassword: `Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.` });
    if (next !== repeat) return setErrors({ repeat: 'Die beiden Passwörter stimmen nicht überein.' });
    change.mutate();
  }

  const fieldError = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Passwort ändern</h2>
      {change.isSuccess && <div className="alert alert--ok" role="status">Ihr Passwort wurde geändert. Anmeldungen auf anderen Geräten sind beendet.</div>}
      {formError && <div className="alert alert--error" role="alert">{formError}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Aktuelles Passwort</span>
          <input className="input" type={show ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" aria-invalid={!!errors.currentPassword} />
          {fieldError('currentPassword')}
        </label>
        <label className="field">
          <span>Neues Passwort</span>
          <input className="input" type={show ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" aria-invalid={!!errors.newPassword} />
          {fieldError('newPassword')}
          <small className="muted">Mindestens {MIN_PASSWORD_LENGTH} Zeichen.</small>
        </label>
        <label className="field">
          <span>Neues Passwort wiederholen</span>
          <input className="input" type={show ? 'text' : 'password'} value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" aria-invalid={!!errors.repeat} />
          {fieldError('repeat')}
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
        <span>Passwörter anzeigen</span>
      </label>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={change.isPending || !current || !next || !repeat}>
          {change.isPending ? 'Wird gespeichert …' : 'Passwort ändern'}
        </button>
      </div>
    </form>
  );
}
